import React, { useState, useEffect } from 'react';
import {
  Shield,
  Phone,
  Mail,
  Globe,
  AlertTriangle,
  Clock,
  Users,
  FileText,
  Download,
  ChevronDown,
  ExternalLink,
  BadgeCheck,
  Siren,
  Building2,
  Scale,
  Clipboard,
  Unplug,
  Briefcase,
  PhoneCall,
  CreditCard,
  Lock,
  CheckCircle2,
  XCircle,
  Loader2,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const getIcon = (iconName) => {
  const icons = {
    'shield': Shield,
    'shield-check': BadgeCheck,
    'gavel': Scale,
    'badge': BadgeCheck,
    'phone-call': PhoneCall,
    'users': Users,
    'credit-card': CreditCard,
    'globe': Globe,
    'lock': Lock,
    'unplug': Unplug,
    'briefcase': Briefcase,
    'clipboard': Clipboard,
    'phone': Phone
  };
  return icons[iconName] || Shield;
};

const StateBreachResponse = ({ assessmentId }) => {
  const [selectedState, setSelectedState] = useState('');
  const [stateData, setStateData] = useState(null);
  const [availableStates, setAvailableStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    stateContacts: true,
    federalContacts: true,
    steps: true,
    requirements: true,
    resources: false
  });

  // Fetch available states on mount
  useEffect(() => {
    fetchAvailableStates();
  }, []);

  // Fetch state data when selection changes
  useEffect(() => {
    if (selectedState) {
      fetchStateData(selectedState);
    } else {
      setStateData(null);
    }
  }, [selectedState]);

  const fetchAvailableStates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/breach-response/states`);
      const data = await response.json();
      setAvailableStates(data.states || []);
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const fetchStateData = async (stateCode) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/breach-response/state/${stateCode}`);
      const data = await response.json();
      setStateData(data);
    } catch (error) {
      console.error('Failed to fetch state data:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!selectedState) return;
    setDownloading(true);
    try {
      const response = await fetch(`${API_URL}/api/breach-response/state/${selectedState}/pdf`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${stateData?.name || selectedState}_Cyber_Incident_Response.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Failed to download PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const ContactCard = ({ contact, type }) => {
    const IconComponent = getIcon(contact.icon);
    const isState = type === 'state';
    
    return (
      <div className={`p-4 rounded-xl border transition-all hover:scale-[1.02] ${
        isState 
          ? 'bg-gradient-to-br from-green-500/10 to-emerald-500/5 border-green-500/30 hover:border-green-500/50'
          : 'bg-gradient-to-br from-blue-500/10 to-indigo-500/5 border-blue-500/30 hover:border-blue-500/50'
      }`}>
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${isState ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
            <IconComponent className={`w-5 h-5 ${isState ? 'text-green-400' : 'text-blue-400'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-white text-sm">{contact.name}</h4>
            {contact.fullName && (
              <p className="text-xs text-slate-400">{contact.fullName}</p>
            )}
            {contact.shortName && (
              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-mono ${
                isState ? 'bg-green-500/20 text-green-300' : 'bg-blue-500/20 text-blue-300'
              }`}>
                {contact.shortName}
              </span>
            )}
            <p className="text-xs text-slate-400 mt-1">{contact.role}</p>
            {contact.region && (
              <p className="text-xs text-slate-500">Region: {contact.region}</p>
            )}
            {contact.coverage && (
              <p className="text-xs text-slate-500">Coverage: {contact.coverage}</p>
            )}
            
            {/* Contact Methods */}
            <div className="mt-3 space-y-2">
              {contact.phone && (
                <a 
                  href={`tel:${contact.phone.replace(/[^0-9+]/g, '')}`}
                  className="flex items-center gap-2 text-sm text-white hover:text-green-400 transition-colors group"
                >
                  <Phone className="w-4 h-4 text-green-400 group-hover:animate-pulse" />
                  <span className="font-mono">{contact.phoneDisplay || contact.phone}</span>
                  {contact.available && (
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
                      contact.available.includes('24/7') 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-slate-700 text-slate-400'
                    }`}>
                      {contact.available}
                    </span>
                  )}
                </a>
              )}
              {contact.email && (
                <a 
                  href={`mailto:${contact.email}`}
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-blue-400 transition-colors"
                >
                  <Mail className="w-4 h-4 text-blue-400" />
                  <span className="truncate">{contact.email}</span>
                </a>
              )}
              {contact.website && (
                <a 
                  href={contact.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-violet-400 transition-colors"
                >
                  <Globe className="w-4 h-4 text-violet-400" />
                  <span className="truncate">Visit Website</span>
                  <ExternalLink className="w-3 h-3 ml-auto" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="state-breach-response" className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-slate-700 bg-gradient-to-r from-red-500/10 to-orange-500/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Siren className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Cyber Incident Emergency Response</h3>
              <p className="text-sm text-slate-400">State-specific breach notification contacts & procedures</p>
            </div>
          </div>
          
          {/* State Selector */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="pl-9 pr-10 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white appearance-none cursor-pointer hover:border-violet-500 transition-colors min-w-[180px]"
              >
                <option value="">Select State...</option>
                {availableStates.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
            
            {selectedState && stateData && (
              <button
                onClick={downloadPDF}
                disabled={downloading}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!selectedState ? (
        <div className="p-8 text-center">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-slate-400 mb-2">Select Your State</h4>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Choose your state from the dropdown to view emergency contacts, breach notification requirements, and step-by-step incident response procedures.
          </p>
        </div>
      ) : loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading {selectedState} breach response data...</p>
        </div>
      ) : stateData ? (
        <div className="p-4 space-y-4">
          {/* State Header */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-xl border border-violet-500/30">
            <div>
              <h4 className="text-xl font-bold text-white">{stateData.name}</h4>
              <p className="text-sm text-slate-400">{stateData.lawReference}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Last Updated</p>
              <p className="text-sm text-slate-300">{stateData.lastUpdated}</p>
            </div>
          </div>

          {/* Notification Timeline Banner */}
          <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <Clock className="w-6 h-6 text-amber-400 flex-shrink-0" />
            <div>
              <h5 className="font-semibold text-amber-300">Notification Timeline</h5>
              <p className="text-sm text-slate-300">{stateData.notificationTimeline?.requirement}</p>
              {stateData.notificationTimeline?.notes && (
                <p className="text-xs text-slate-400 mt-1">{stateData.notificationTimeline.notes}</p>
              )}
            </div>
          </div>

          {/* State Level Contacts */}
          <div className="rounded-xl border border-green-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('stateContacts')}
              className="w-full flex items-center justify-between p-4 bg-green-500/10 hover:bg-green-500/15 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-green-400" />
                <span className="font-semibold text-green-300">State Level Emergency Contacts</span>
                <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                  {stateData.contacts?.state?.length || 0} contacts
                </span>
              </div>
              <ChevronDown className={`w-5 h-5 text-green-400 transition-transform ${expandedSections.stateContacts ? 'rotate-180' : ''}`} />
            </button>
            {expandedSections.stateContacts && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {stateData.contacts?.state?.map((contact, idx) => (
                  <ContactCard key={idx} contact={contact} type="state" />
                ))}
              </div>
            )}
          </div>

          {/* Federal Level Contacts */}
          <div className="rounded-xl border border-blue-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('federalContacts')}
              className="w-full flex items-center justify-between p-4 bg-blue-500/10 hover:bg-blue-500/15 transition-colors"
            >
              <div className="flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-blue-400" />
                <span className="font-semibold text-blue-300">Federal Level Contacts</span>
                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                  {stateData.contacts?.federal?.length || 0} contacts
                </span>
              </div>
              <ChevronDown className={`w-5 h-5 text-blue-400 transition-transform ${expandedSections.federalContacts ? 'rotate-180' : ''}`} />
            </button>
            {expandedSections.federalContacts && (
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {stateData.contacts?.federal?.map((contact, idx) => (
                  <ContactCard key={idx} contact={contact} type="federal" />
                ))}
              </div>
            )}
          </div>

          {/* Online Reporting */}
          {stateData.contacts?.reporting && stateData.contacts.reporting.length > 0 && (
            <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
              <h5 className="font-semibold text-purple-300 mb-3 flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Online Reporting Resources
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {stateData.contacts.reporting.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg hover:bg-purple-500/10 transition-colors group"
                  >
                    <Lock className="w-5 h-5 text-purple-400" />
                    <div className="flex-1">
                      <p className="font-medium text-white text-sm">{resource.name}</p>
                      <p className="text-xs text-slate-400">{resource.description}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-purple-400" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Immediate Response Steps */}
          <div className="rounded-xl border border-orange-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('steps')}
              className="w-full flex items-center justify-between p-4 bg-orange-500/10 hover:bg-orange-500/15 transition-colors"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                <span className="font-semibold text-orange-300">Immediate Response Steps</span>
              </div>
              <ChevronDown className={`w-5 h-5 text-orange-400 transition-transform ${expandedSections.steps ? 'rotate-180' : ''}`} />
            </button>
            {expandedSections.steps && (
              <div className="p-4 space-y-3">
                {stateData.immediateSteps?.map((step, idx) => {
                  const IconComponent = getIcon(step.icon);
                  return (
                    <div key={idx} className="flex gap-4 p-4 bg-slate-900/50 rounded-lg">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                          <span className="text-lg font-bold text-orange-400">{step.step}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h6 className="font-semibold text-white flex items-center gap-2">
                          <IconComponent className="w-4 h-4 text-orange-400" />
                          {step.title}
                        </h6>
                        <p className="text-sm text-slate-300 mt-1">{step.description}</p>
                        {step.warning && (
                          <div className="mt-2 flex items-start gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-red-300">{step.warning}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notification Requirements */}
          <div className="rounded-xl border border-cyan-500/30 overflow-hidden">
            <button
              onClick={() => toggleSection('requirements')}
              className="w-full flex items-center justify-between p-4 bg-cyan-500/10 hover:bg-cyan-500/15 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                <span className="font-semibold text-cyan-300">Notification Requirements</span>
              </div>
              <ChevronDown className={`w-5 h-5 text-cyan-400 transition-transform ${expandedSections.requirements ? 'rotate-180' : ''}`} />
            </button>
            {expandedSections.requirements && (
              <div className="p-4 space-y-3">
                {stateData.notificationRequirements?.map((req, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h6 className="font-medium text-white">{req.recipient}</h6>
                      <p className="text-sm text-slate-400">Threshold: {req.threshold}</p>
                      <p className="text-sm text-slate-400">Method: {req.method}</p>
                      {req.notes && (
                        <p className="text-xs text-slate-500 mt-1 italic">{req.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Additional Resources */}
          {stateData.resources && stateData.resources.length > 0 && (
            <div className="rounded-xl border border-emerald-500/30 overflow-hidden">
              <button
                onClick={() => toggleSection('resources')}
                className="w-full flex items-center justify-between p-4 bg-emerald-500/10 hover:bg-emerald-500/15 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-emerald-400" />
                  <span className="font-semibold text-emerald-300">Additional Resources</span>
                </div>
                <ChevronDown className={`w-5 h-5 text-emerald-400 transition-transform ${expandedSections.resources ? 'rotate-180' : ''}`} />
              </button>
              {expandedSections.resources && (
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {stateData.resources.map((resource, idx) => (
                    <a
                      key={idx}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg hover:bg-emerald-500/10 transition-colors group"
                    >
                      <ExternalLink className="w-5 h-5 text-emerald-400" />
                      <div className="flex-1">
                        <p className="font-medium text-white text-sm">{resource.name}</p>
                        <p className="text-xs text-slate-400">{resource.description}</p>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Legal Definition */}
          {stateData.personalInfoDefinition && (
            <div className="p-4 bg-slate-900/50 border border-slate-700 rounded-xl">
              <h5 className="font-semibold text-slate-300 mb-2 flex items-center gap-2">
                <Scale className="w-4 h-4" />
                Personal Information Definition
              </h5>
              <p className="text-sm text-slate-400">{stateData.personalInfoDefinition}</p>
              {stateData.encryptionException && (
                <p className="text-xs text-amber-400 mt-2">Note: {stateData.encryptionException}</p>
              )}
              {stateData.riskAssessmentException && (
                <p className="text-xs text-amber-400 mt-2">Exception: {stateData.riskAssessmentException}</p>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default StateBreachResponse;
